import { WSLoader } from "@wingspanhq/fe-component-library";
import { FundingSourceType } from "@wingspanhq/payments/dist/interfaces";
import { AccountStatus, Currency } from "@wingspanhq/users/dist/lib/interfaces";
import { useMemo } from "react";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_PAYROLL_SETTINGS } from "../../../../query/payments/keys";
import { useQueryPayrollSettings } from "../../../../query/payments/queries";
import {
  useQueryAccounts,
  useClientQuery
} from "../../../../query/users/queries";
import { paymentsService } from "../../../../services/payments";
import { FUNDING_SOURCE_TYPE_STRIPE_PAYMENT_CARD } from "../../../../shared/constants/fundingSource";
import { selectorAccountVerificationPending } from "../../../../shared/selectors/selectorAccountVerificationPending";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorFundingSourceMethodType } from "../../../../shared/selectors/selectorFundingSourceMethodType";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { ManageMethods } from "../ManageMethods";
import { MethodType, MethodValue } from "../ManageMethods/types";

function methodTypeToFundingSourceType(methodType: MethodType) {
  switch (methodType) {
    case "Account":
      return FundingSourceType.Account;

    case "InternalAccount":
      return FundingSourceType.InternalAccount;

    // Cards offered here are Stripe saved payment methods, so they have to be saved as
    // `StripePaymentCard`. Saving them as `PaymentCard` makes funds movements route the
    // Stripe paymentMethod id over the generic card rail, which can't charge it.
    case "CreditCard":
      return FUNDING_SOURCE_TYPE_STRIPE_PAYMENT_CARD;

    default:
      throw new Error("Not supported");
  }
}

type Props = {
  onNext: () => void;
  onAddNew: () => void;
};

export const ManageFundingMethod: React.FC<Props> = ({ onNext, onAddNew }) => {
  const userId = useUserId();
  const queryAccounts = useQueryAccounts();
  const queryPayrollSettings = useQueryPayrollSettings(userId);
  const queryInternalAccounts = useQueryInternalAccounts("Banking");
  const queryClient = useClientQuery(userId);

  const accounts = useMemo(
    () =>
      (queryAccounts.data || []).filter(
        account =>
          (account.canBeUsedFor?.payments ||
            selectorAccountVerificationPending(account)) &&
          account.status !== AccountStatus.Inactive
      ),
    [queryAccounts.data]
  );

  const creditCards = useMemo(
    () => queryClient.data?.profile.savedPaymentMethods,
    [queryClient.data?.profile.savedPaymentMethods]
  );

  const internalAccounts = useMemo(
    () =>
      (queryInternalAccounts.data || []).filter(selectorActiveBankingAccount),
    [queryInternalAccounts.data]
  );

  const initialValue = useMemo((): MethodValue | undefined => {
    const fundingSource = queryPayrollSettings.data?.fundingSource;

    if (!fundingSource) {
      return;
    }

    const type = selectorFundingSourceMethodType(
      fundingSource.fundingSourceType
    );

    if (!type) {
      return;
    }

    return {
      id: fundingSource.fundingSourceId,
      type
    };
  }, [queryPayrollSettings.data?.fundingSource]);

  const [mutate, meta] = useWSMutation<
    void,
    WSServiceError,
    MethodValue | null
  >(
    async data => {
      if (!data) {
        return;
      }

      await paymentsService.payrollSettings.update(userId, {
        fundingSource: {
          fundingSourceId: data.id,
          fundingSourceType: methodTypeToFundingSourceType(data.type),
          fundingSourceCurrency: Currency.USD
        }
      });
    },
    { onSuccess: onNext, dependencies: [QUERY_PAYROLL_SETTINGS] }
  );

  if (
    queryAccounts.isLoading ||
    queryPayrollSettings.isLoading ||
    queryInternalAccounts.isLoading
  ) {
    return <WSLoader.Spinner />;
  }

  return (
    <ManageMethods
      data={{
        accounts,
        internalAccounts,
        creditCards
      }}
      header={{
        label: {
          text: "Manage payroll funding method",
          helperText:
            "Set an existing payroll funding method as default or add a new payroll funding method"
        }
      }}
      onAddNew={onAddNew}
      addNewLabel="Add new payroll funding method"
      initialValue={initialValue}
      onSubmit={mutate}
      error={meta.error}
      isLoading={meta.isLoading}
    />
  );
};
